import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Mail, Phone, Instagram, ArrowRight } from "lucide-react";
import { CONTACT } from "../data/experiences";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY_FORM = { name: "", email: "", travel_dates: "", group_size: "", interests: "", message: "" };

const ContactDetails = () => (
  <aside data-testid="contact-details-card">
    <h2 className="editorial-heading mb-6">A conversation starts here.</h2>
    <p className="editorial-body mb-8">Prefer to write directly? Find us here.</p>
    <div className="space-y-5 text-sm text-maroon">
      <a data-testid="contact-email-link" href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 break-all"><Mail size={18} className="shrink-0" /> {CONTACT.email}</a>
      <a data-testid="contact-whatsapp-link" href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3"><Phone size={18} className="shrink-0" /> {CONTACT.phone} (WhatsApp)</a>
      <a data-testid="contact-instagram-link" href={CONTACT.instagramUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3"><Instagram size={18} className="shrink-0" /> {CONTACT.instagram}</a>
    </div>
    <p className="editorial-body border-t border-maroon/20 pt-7 mt-9" data-testid="contact-arrangements-note">The team will respond to discuss arrangements. Sending an enquiry is not a confirmed reservation or payment.</p>
  </aside>
);

export default function Contact() {
  const [params] = useSearchParams();
  const context = params.get("experience") || params.get("interest") || "";
  const [form, setForm] = useState({ ...EMPTY_FORM, interests: context });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setForm((current) => ({ ...current, interests: context }));
    setSent(false);
    setError("");
  }, [context]);

  const change = (event) => setForm({ ...form, [event.target.name]: event.target.value });
  const submit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/inquiries`, {
        ...form,
        journey_type: form.interests || "General enquiry",
        subject: `Visit enquiry: ${form.interests || "Unzip Delhi"}`,
      });
      setSent(true);
    } catch {
      setError(`We couldn’t send your enquiry. Please try again or email ${CONTACT.email} directly.`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page" className="bg-ivory">
      <header className="editorial-container page-intro">
        <p className="editorial-label mb-6">CONTACT</p>
        <h1 className="font-accent text-4xl sm:text-5xl lg:text-6xl text-maroon" data-testid="contact-headline">Let’s Plan Your Visit</h1>
        <p className="editorial-body mt-6 max-w-2xl">Whether you’re an individual traveller, an educator or planning for a group, tell us what you would like to explore. Dates and group details can be tentative.</p>
      </header>
      <div className="editorial-container contact-layout">
        <div>
          {sent ? (
            <div role="status" className="border-y border-maroon/25 py-10" data-testid="contact-success">
              <p className="editorial-label mb-5">ENQUIRY RECEIVED</p>
              <h2 className="editorial-heading mb-5">Your note is with us.</h2>
              <p className="editorial-body" data-testid="contact-success-message">Thank you for writing to Unzip Delhi. The team will be in touch to discuss your visit. This is an enquiry, not a confirmed booking or payment.</p>
              <button type="button" className="editorial-button mt-8" data-testid="contact-another-button" onClick={() => { setForm({ ...EMPTY_FORM, interests: context }); setSent(false); }}>Send another enquiry</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-7" data-testid="contact-form">
              {context && <p className="border-l-2 border-maroon pl-4 editorial-body" data-testid="contact-enquiry-context">Your interest: <strong>{context}</strong>. You can edit this below.</p>}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <label className="contact-field" htmlFor="contact-name">Name <span aria-hidden="true">*</span><input id="contact-name" name="name" data-testid="contact-name-input" required autoComplete="name" value={form.name} onChange={change} className="contact-input" /></label>
                <label className="contact-field" htmlFor="contact-email">Email <span aria-hidden="true">*</span><input id="contact-email" name="email" data-testid="contact-email-input" required type="email" autoComplete="email" value={form.email} onChange={change} className="contact-input" /></label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <label className="contact-field" htmlFor="contact-dates">Intended dates (optional)<input id="contact-dates" name="travel_dates" data-testid="contact-dates-input" placeholder="A date, month, or not sure yet" value={form.travel_dates} onChange={change} className="contact-input" /></label>
                <label className="contact-field" htmlFor="contact-group-size">Approximate group size (optional)<input id="contact-group-size" name="group_size" data-testid="contact-group-size-input" placeholder="Just you, a few friends, a group…" value={form.group_size} onChange={change} className="contact-input" /></label>
              </div>
              <label className="contact-field" htmlFor="contact-interests">What would you like to explore? (optional)<input id="contact-interests" name="interests" data-testid="contact-interests-input" placeholder="A neighbourhood, a story, an interest…" value={form.interests} onChange={change} className="contact-input" /></label>
              <label className="contact-field" htmlFor="contact-message">Tell us a little more <span aria-hidden="true">*</span><textarea id="contact-message" name="message" data-testid="contact-message-input" required rows={5} placeholder="Your curiosity, plans or questions. It’s fine if you’re still deciding." value={form.message} onChange={change} className="contact-input resize-y" /></label>
              <p className="text-xs leading-relaxed text-obsidian/70" data-testid="contact-booking-disclaimer">This form sends an enquiry. It does not confirm a reservation or take a payment.</p>
              {error && <p role="alert" className="text-sm text-maroon" data-testid="contact-error">{error}</p>}
              <button type="submit" disabled={submitting} className="editorial-button" data-testid="contact-submit-button">{submitting ? "Sending…" : "Send enquiry"} <ArrowRight size={16} /></button>
            </form>
          )}
        </div>
        <ContactDetails />
      </div>
    </main>
  );
}
